

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Plus, Edit2, Trash2, X, Paperclip, Upload, ExternalLink, Loader2, Eye, FileText, Download } from "lucide-react";
import { toast } from "../../lib/toast";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { cn } from "../../lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { MeetingEditorSkeleton } from "../../components/LoadingShimmer";
import MeetingOnboarding from "../../components/MeetingOnboarding";
import MeetingGuide from "../../components/MeetingGuide";
import { SaveStatusIndicator } from "../../components/SaveStatusIndicator";
import { UnsavedChangesDialog } from "../../components/UnsavedChangesDialog";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { InputDialog } from "../../components/InputDialog";
import { useCharLimitHandlers } from "../../components/CharLimitIndicator";
import { LIMITS } from "../../lib/constants";
import { useAutoSave } from "../../hooks/useAutoSave";
import * as meetingService from "../../services/meeting.service";
import * as projectService from "../../services/project.service";
import type { AtaAttachment, Meeting } from "../../services/meeting.service";
import { uploadService } from "../../services/upload.service";
import { ProjectCodeModal } from "../../components/ProjectCodeModal";


import { useMeetingData } from "./hooks/useMeetingData";
import { useTopics } from "./hooks/useTopics";
import { useItems } from "./hooks/useItems";
import { useParticipants } from "./hooks/useParticipants";
import { useAtaImagens } from "./hooks/useAtaImagens";
import { useContatosProjeto } from "../../hooks/useContatosProjeto";
import { usePageTitle } from "../../hooks/usePageTitle";
import { useAuth } from "../../contexts/AuthContext";
import {
  getProjectCollaborators,
  type ProjectCollaborator,
} from "../../services/participante.service";
import { listShares } from "../../services/sharing.service";


import { MeetingHeader } from "./components/MeetingHeader";
import {
  mergeResponsibleOptions,
  type ResponsibleSource,
} from "./components/MeetingMetadataFields";
import { TopicSidebar } from "./components/TopicSidebar";
import { TopicSection } from "./components/TopicSection";
import { ItemEditor } from "./components/ItemEditor";
import { ParticipantDrawer } from "./components/ParticipantDrawer";
import { TopicHistoryDrawer } from "./components/TopicHistoryDrawer";
import { AtaGaleriaEditor } from "./components/AtaGaleriaEditor";
import { Header } from "../../components/Header";
import type { Topico } from "../../services/topico.service";
import {
  mapEditorStateToAutoSave,
  mergeRefetchedMeetingState,
  type MeetingEditorState,
} from "./meetingEditor.mapping";

function AnexoViewerModal({
  ataId,
  item,
  open,
  onClose,
}: {
  ataId?: string;
  item: AnexoItem;
  open: boolean;
  onClose: () => void;
}) {
  const [signedUrl, setSignedUrl] = React.useState<string | null>(null);
  const [loadingUrl, setLoadingUrl] = React.useState(false);

  React.useEffect(() => {
    if (!open || !item.url || !ataId) return;
    setSignedUrl(null);
    setLoadingUrl(true);
    uploadService
      .getAnexoSignedUrl(ataId, item.url)
      .then((url) => setSignedUrl(url))
      .catch(() => setSignedUrl(item.url ?? null))
      .finally(() => setLoadingUrl(false));
  }, [ataId, open, item.url]);

  if (!item.url) return null;

  const ext = item.url.split("?")[0].split(".").pop()?.toLowerCase() ?? "";
  const isImage = ["jpg", "jpeg", "png", "gif", "webp"].includes(ext);
  const isPdf = ext === "pdf";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl w-full h-[85vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-5 pt-4 pb-3 border-b border-border shrink-0">
          <DialogTitle className="flex items-center gap-2 text-sm font-medium text-foreground">
            <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="truncate">{item.nome || "Anexo"}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-hidden relative bg-muted/30">
          {loadingUrl && (
            <div className="flex items-center justify-center h-full gap-2 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span className="text-sm">Carregando...</span>
            </div>
          )}
          {!loadingUrl && signedUrl && isPdf && (
            <iframe
              src={signedUrl}
              title={item.nome}
              className="w-full h-full border-0"
            />
          )}
          {!loadingUrl && signedUrl && isImage && (
            <div className="w-full h-full flex items-center justify-center p-4">
              <img
                src={signedUrl}
                alt={item.nome}
                className="max-w-full max-h-full object-contain rounded-lg shadow"
              />
            </div>
          )}
          {!loadingUrl && signedUrl && !isPdf && !isImage && (
            <div className="flex flex-col items-center justify-center h-full gap-4 text-center p-8">
              <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center">
                <FileText className="h-8 w-8 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground mb-1">{item.nome}</p>
                <p className="text-xs text-muted-foreground">
                  Visualização não disponível para este tipo de arquivo.
                </p>
              </div>
              <a
                href={signedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Download className="h-4 w-4" />
                Baixar arquivo
              </a>
            </div>
          )}
        </div>

        {/* Rodapé com botão de abrir externo */}
        <div className="px-5 py-3 border-t border-border flex justify-end shrink-0">
          {signedUrl && (
            <a
              href={signedUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Abrir em nova aba
            </a>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Componente auxiliar para cada linha de anexo
// ──────────────────────────────────────────────────────────────────────────────
type AnexoItem = AtaAttachment;

type MeetingSaveUpdates = Partial<Omit<Meeting, "id" | "project_id">>;
type MeetingAutoSaveUpdates = MeetingSaveUpdates & {
  readonly _changeTimestamp?: number;
};

interface AnexoRowProps {
  ataId?: string;
  item: AnexoItem;
  idx: number;
  isDisabled: boolean;
  autoFocus: boolean;
  onNomeChange: (value: string) => void;
  onNomeBlur: () => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onUpload: (file: File) => Promise<void>;
  onRemove: () => void;
}

function AnexoRow({
  ataId,
  item,
  isDisabled,
  autoFocus,
  onNomeChange,
  onNomeBlur,
  onKeyDown,
  onUpload,
  onRemove,
}: AnexoRowProps) {
  const { t } = useTranslation("common");
  const [uploading, setUploading] = React.useState(false);
  const [viewerOpen, setViewerOpen] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await onUpload(file);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <>
      <div
        style={{ animation: "fadeIn 0.18s ease-out" }}
        className="group flex items-center gap-3 px-4 py-2.5 bg-muted/40 hover:bg-muted/70 focus-within:bg-primary/5 focus-within:ring-1 focus-within:ring-primary/30 rounded-xl transition-all duration-150"
      >
        {/* Ícone / botão de visualizar */}
        {item.url ? (
          <button
            type="button"
            onClick={() => setViewerOpen(true)}
            className="shrink-0 text-blue-500 hover:text-blue-700 transition-colors"
            title={t("editor.attachments_view", "Visualizar arquivo")}
          >
            <Eye className="h-3.5 w-3.5" />
          </button>
        ) : (
          <Paperclip className="h-3.5 w-3.5 text-muted-foreground/40 group-focus-within:text-primary shrink-0 transition-colors duration-150" />
        )}

        <input
          type="text"
          value={item.nome}
          onChange={(e) => onNomeChange(e.target.value)}
          onBlur={onNomeBlur}
          onKeyDown={onKeyDown}
          placeholder={t("editor.attachments_item_placeholder", "Nome do anexo...")}
          className="flex-1 text-sm bg-transparent border-0 outline-none focus:ring-0 p-0 text-foreground placeholder:text-muted-foreground/40"
          disabled={isDisabled}
          autoFocus={autoFocus}
        />

        {!isDisabled && (
          <>
            {/* Botão de upload - sempre visível */}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className={`shrink-0 flex items-center gap-1 px-2 py-1 rounded-lg transition-all duration-150 text-xs font-medium ${
                item.url
                  ? "text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                  : "text-muted-foreground hover:text-primary hover:bg-primary/5 border border-dashed border-border hover:border-primary/50"
              } active:scale-95 disabled:opacity-50`}
              title={
                item.url
                  ? t("editor.attachments_replace", "Substituir arquivo")
                  : t("editor.attachments_attach", "Anexar arquivo")
              }
            >
              {uploading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Upload className="h-3.5 w-3.5" />
              )}
              {!item.url && !uploading && (
                <span className="whitespace-nowrap">Anexar</span>
              )}
            </button>

            {/* Botão remover - sempre visível */}
            <button
              type="button"
              onClick={onRemove}
              className="shrink-0 p-1.5 rounded-lg text-muted-foreground/60 hover:text-destructive hover:bg-destructive/10 active:scale-95 transition-all duration-150"
              title={t("editor.attachments_remove", "Remover")}
            >
              <X className="h-3.5 w-3.5" />
            </button>

            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,image/*"
              onChange={handleFileChange}
              className="hidden"
            />
          </>
        )}
      </div>

      {item.url && (
        <AnexoViewerModal
          ataId={ataId}
          item={item}
          open={viewerOpen}
          onClose={() => setViewerOpen(false)}
        />
      )}
    </>
  );
}

export default function MeetingEditor() {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const { id: projectId, meetingId } = useParams<{
    id: string;
    meetingId: string;
  }>();
  const navigate = useNavigate();


  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem("sidebar-collapsed") === "true";
  });
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [guideStep, setGuideStep] = useState<number>(-1);
  const [newTopicoTitle, setNewTopicoTitle] = useState("");
  const [historyDrawerTopic, setHistoryDrawerTopic] = useState<Topico | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreatingTopic, setIsCreatingTopic] = useState(false);
  const [activeSection, setActiveSection] = useState<string>("header");
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(
    null
  );
  const [showFinalizeDialog, setShowFinalizeDialog] = useState(false);
  const [showProjectCodeModal, setShowProjectCodeModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    type: 'participant' | 'topic' | 'item' | 'desenvolvimento';
    id: string;
    message: string;
    topicoNome?: string;
    topicoItens?: string[];
  } | null>(null);
  const [isAddingAnexo, setIsAddingAnexo] = useState(false);

  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const novoAnexoFileInputRef = useRef<HTMLInputElement>(null);
  const newTopicInputRef = useRef<HTMLInputElement>(null);
  const inputValueRef = useRef<string>("");
  const topicosRef = useRef<Topico[]>([]);
  const scrollListenerRef = useRef<{ element: Element; listener: () => void } | null>(null);
  const lastProgrammaticScrollTime = useRef(0);


  useEffect(() => {
    if (sidebarCollapsed) {
      document.body.classList.add("sidebar-collapsed");
    } else {
      document.body.classList.remove("sidebar-collapsed");
    }
  }, [sidebarCollapsed]);


  useEffect(() => {
    if (isModalOpen) {
      setTimeout(() => {
        newTopicInputRef.current?.focus();
      }, 100);
    }
  }, [isModalOpen]);

  if (!projectId || !meetingId) {
    navigate("/dashboard");
    return null;
  }


  // Global change tracker: rastreia mudanças de TODOS os hooks (items, developments, topics, participantes)
  // Este timestamp é atualizado sempre que qualquer hook faz uma alteração, forçando o autosave a reagir
  const [lastChangeTimestamp, setLastChangeTimestamp] = useState<number>(0);

  // Callback compartilhado: todos os hooks chamam isso após qualquer operação de mudança
  const notifyChange = useCallback(() => {
    setLastChangeTimestamp(Date.now());
  }, []);

  const {
    meeting,
    project,
    topicos: initialTopicos,
    items: initialItems,
    participantes: initialParticipantes,
    desenvolvimentos: initialDesenvolvimentos,
    loading,
    error,
    lastMeetingNumber,
    permission,
    refetch,
  } = useMeetingData({
    projectId,
    meetingId,
  });

  // Verificar se usuário pode editar
  const canEdit = permission.canEdit;
  const [remoteResponsibleSources, setRemoteResponsibleSources] = useState<
    readonly ResponsibleSource[]
  >([]);
  usePageTitle(meeting ? `Editar — ${meeting.name || `Ata #${meeting.number}`}` : 'Editor de Ata');

  useEffect(() => {
    let isActive = true;

    void Promise.allSettled([
      getProjectCollaborators(projectId),
      listShares("project", Number(projectId)),
    ]).then(([collaboratorsResult, sharesResult]) => {
      if (!isActive) return;

      const collaborators: readonly ProjectCollaborator[] =
        collaboratorsResult.status === "fulfilled" ? collaboratorsResult.value : [];
      const collaboratorSources = collaborators.flatMap((collaborator) =>
        collaborator.user_id === null
          ? []
          : [{
              id: collaborator.user_id,
              name: collaborator.nome,
              email: collaborator.email,
            }]
      );
      const ownerSources = sharesResult.status === "fulfilled"
        ? sharesResult.value.flatMap((share) =>
            share.origin === "project_owner" && share.user_id !== null && share.user_id !== undefined
              ? [{ id: share.user_id, name: null, email: share.user_email }]
              : []
          )
        : [];

      setRemoteResponsibleSources([...ownerSources, ...collaboratorSources]);
    });

    return () => {
      isActive = false;
    };
  }, [projectId]);

  const responsibleOptions = useMemo(() => {
    const authenticatedUserId = Number(user?.id);
    return mergeResponsibleOptions([
      ...(user && Number.isInteger(authenticatedUserId) && authenticatedUserId > 0
        ? [{ id: authenticatedUserId, name: user.name, email: user.email }]
        : []),
      ...remoteResponsibleSources,
      ...(meeting?.responsavel_id
        ? [{
            id: meeting.responsavel_id,
            name: meeting.responsavel_nome,
            email: null,
          }]
        : []),
    ]);
  }, [meeting?.responsavel_id, meeting?.responsavel_nome, remoteResponsibleSources, user]);


  const topicsHook = useTopics({
    ataId: meetingId,
    initialTopicos,
    onUpdate: refetch
  });


  useEffect(() => {
    topicosRef.current = topicsHook.topicos;
  }, [topicsHook.topicos]);

  // Scroll listener para detectar qual seção está visível
  useEffect(() => {
    if (loading) return;

    // Limpar listener anterior se existir
    if (scrollListenerRef.current) {
      scrollListenerRef.current.element.removeEventListener("scroll", scrollListenerRef.current.listener);
      scrollListenerRef.current = null;
    }

    const timeoutId = setTimeout(() => {
      const mainElement = document.querySelector("main.overflow-y-auto");
      if (!mainElement) return;

      const handleScroll = () => {
        // Ignorar durante scroll programático (600ms após clique na sidebar)
        if (Date.now() - lastProgrammaticScrollTime.current < 600) return;

        // Limpar seleção por clique quando usuário scrolla manualmente
        topicsHook.setSelectedTopicId(null);

        const mainRect = mainElement.getBoundingClientRect();
        const viewportHeight = mainElement.clientHeight;
        const scrollTop = mainElement.scrollTop;
        const scrollHeight = mainElement.scrollHeight;

        // Verificar se chegou ao final do scroll (com tolerância de 5px)
        const isAtEnd = scrollTop + viewportHeight >= scrollHeight - 5;

        const sections: { id: string; top: number }[] = [];

        const headerElement = document.getElementById("header-section");
        if (headerElement) {
          const rect = headerElement.getBoundingClientRect();
          sections.push({ id: "header", top: rect.top - mainRect.top });
        }

        topicosRef.current.forEach((topico) => {
          const element = document.getElementById(`topic-${topico.id}`);
          if (element) {
            const rect = element.getBoundingClientRect();
            sections.push({ id: topico.id, top: rect.top - mainRect.top });
          }
        });

        if (sections.length === 0) return;

        // Quando no final do scroll, aumentar threshold para detectar últimos tópicos
        const threshold = isAtEnd ? viewportHeight * 0.7 : 150;
        let activeId = "header";
        let bestTop = -Infinity;

        for (const section of sections) {
          // Encontrar a seção cujo topo está mais próximo de 0
          // entre as que já passaram do threshold
          if (section.top <= threshold && section.top > bestTop) {
            bestTop = section.top;
            activeId = section.id;
          }
        }

        setActiveSection(activeId);
      };

      // Executar imediatamente
      handleScroll();

      // Criar listener com throttle
      let ticking = false;
      const scrollListener = () => {
        if (!ticking) {
          requestAnimationFrame(() => {
            handleScroll();
            ticking = false;
          });
          ticking = true;
        }
      };

      mainElement.addEventListener("scroll", scrollListener, { passive: true });
      scrollListenerRef.current = { element: mainElement, listener: scrollListener };
    }, 100);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [loading]);

  // Cleanup no unmount
  useEffect(() => {
    return () => {
      if (scrollListenerRef.current) {
        scrollListenerRef.current.element.removeEventListener("scroll", scrollListenerRef.current.listener);
      }
    };
  }, []);


  useEffect(() => {
    if (!topicsHook.selectedTopicId) return;

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // Marcar timestamp e setar activeSection diretamente
    lastProgrammaticScrollTime.current = Date.now();
    setActiveSection(topicsHook.selectedTopicId);

    scrollTimeoutRef.current = setTimeout(() => {
      const element = document.getElementById(
        `topic-${topicsHook.selectedTopicId}`
      );
      const mainElement = document.querySelector("main.overflow-y-auto");

      if (element && mainElement) {
        const elementRect = element.getBoundingClientRect();
        const mainRect = mainElement.getBoundingClientRect();
        const scrollPosition =
          mainElement.scrollTop + elementRect.top - mainRect.top - 80;

        mainElement.scrollTo({
          top: scrollPosition,
          behavior: "smooth",
        });
      } else if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
        window.scrollBy(0, -80);
      }
    }, 100);

    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [topicsHook.selectedTopicId]);


  const [meetingLocalState, setMeetingLocalState] = useState<MeetingEditorState>({
    name: "",
    number: 0,
    date: "",
    location: "",
    legenda: "",
    observacoes: "",
    anexos: [],
    responsavel_id: null,
    hora_inicio: "",
    hora_fim: "",
  });
  const lastServerMeetingStateRef = useRef<{
    readonly meetingId: string;
    readonly state: MeetingEditorState;
  } | null>(null);

  const itemsHook = useItems({
    ataId: meetingId,
    meetingNumber: meeting?.number || 0,
    meetingDate: meetingLocalState.date || meeting?.date || "",
    initialItems,
    initialDesenvolvimentos,
    onUpdate: refetch,
    onChangeDetected: notifyChange, // Notifica o sistema de autosave sobre mudanças
  });


  const { contatos } = useContatosProjeto(projectId);

  // Flag para indicar que os dados iniciais do meeting foram sincronizados com o estado local
  // Isso evita que o autosave mostre "Editando" logo ao entrar na ata
  const [isDataSynced, setIsDataSynced] = useState(false);

  const participantsHook = useParticipants({
    ataId: meetingId,
    initialParticipantes,
    onUpdate: refetch,
    onChangeDetected: notifyChange,
    onParticipanteAdicionado: () => {

    },
  });

  const imagensHook = useAtaImagens({
    ataId: meetingId || "",
  });


  useEffect(() => {
    if (meeting) {
      const newState = {
        name: meeting.name || "",
        number: meeting.number || 0,
        date: meeting.date || "",
        location: meeting.location || "",
        legenda: meeting.legenda || "",
        observacoes: meeting.observacoes || "",
        anexos: meeting.anexos.map((item) =>
          item.url === undefined
            ? { nome: item.nome }
            : { nome: item.nome, url: item.url }
        ),
        responsavel_id: meeting.responsavel_id ?? null,
        hora_inicio: meeting.hora_inicio || "",
        hora_fim: meeting.hora_fim || "",
      };

      setMeetingLocalState((prev) => {
        const previousServerState = lastServerMeetingStateRef.current?.meetingId === meeting.id
          ? lastServerMeetingStateRef.current.state
          : null;
        const mergedState = mergeRefetchedMeetingState(
          prev,
          previousServerState,
          newState
        );
        lastServerMeetingStateRef.current = {
          meetingId: meeting.id,
          state: newState,
        };

        if (
          prev.name === mergedState.name &&
          prev.number === mergedState.number &&
          prev.date === mergedState.date &&
          prev.location === mergedState.location &&
          prev.legenda === mergedState.legenda &&
          prev.observacoes === mergedState.observacoes &&
          prev.responsavel_id === mergedState.responsavel_id &&
          prev.anexos.length === mergedState.anexos.length &&
          prev.anexos.every((item, index) =>
            item.nome === mergedState.anexos[index]?.nome &&
            item.url === mergedState.anexos[index]?.url
          ) &&
          prev.hora_inicio === mergedState.hora_inicio &&
          prev.hora_fim === mergedState.hora_fim
        ) {
          setIsDataSynced(true);
          return prev;
        }
        return mergedState;
      });

      // Marca como sincronizado após atualizar o estado local
      setIsDataSynced(true);
    }
  }, [meeting]);


  const handleSaveMeeting = useCallback(
    async (updates: MeetingAutoSaveUpdates) => {
      if (!meeting) return;

      try {
        // Remove o campo _changeTimestamp antes de salvar (é apenas para controle interno)
        const { _changeTimestamp, ...dataToSave } = updates;

        const oldNumber = meeting.number;
        const oldDate = meeting.date;
        await meetingService.updateMeeting(meetingId, dataToSave);


        if (dataToSave.number !== undefined && dataToSave.number !== oldNumber) {
          itemsHook.syncAtaOrigemNumero(oldNumber, dataToSave.number);
          await refetch();
        }

        if (dataToSave.date !== undefined && dataToSave.date !== oldDate) {
          itemsHook.syncAtaOrigemData(dataToSave.date);
        }

      } catch (error) {
        const msg = error instanceof Error ? error.message : '';
        if (msg.includes('Network') || msg.includes('network') || msg.includes('ERR_NETWORK')) {
          toast.error('Não foi possível salvar — verifique sua conexão com a internet.');
        } else {
          toast.error(msg || 'Não foi possível salvar as alterações. Verifique os dados e tente novamente.');
        }
        throw error;
      }
    },
    [meeting, meetingId, itemsHook, refetch]
  );



  // AutoSave data: cabeçalho da reunião + timestamp de mudanças globais
  // O timestamp força o autosave a detectar mudanças em items, developments, topics e participantes
  const autoSaveData = useMemo(
    () => meeting
      ? mapEditorStateToAutoSave(meetingLocalState, lastChangeTimestamp)
      : null,
    [meeting, meetingLocalState.name, meetingLocalState.number, meetingLocalState.date, meetingLocalState.location, meetingLocalState.legenda, meetingLocalState.observacoes, meetingLocalState.anexos, meetingLocalState.responsavel_id, meetingLocalState.hora_inicio, meetingLocalState.hora_fim, lastChangeTimestamp]
  );

  const {
    status: saveStatus,
    hasUnsavedChanges,
    forceSave,
  } = useAutoSave({
    data: autoSaveData,
    onSave: async (data) => {
      if (data) {
        await handleSaveMeeting(data);
      }
    },
    // Só habilita o autosave após os dados iniciais serem sincronizados
    // Isso evita que mostre "Editando" logo ao entrar na ata
    enabled: !!meeting && isDataSynced,
    delay: 5000,
    maxWait: 120000,
  });


  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);


  const navigateWithCheck = (path: string) => {
    if (hasUnsavedChanges) {
      setPendingNavigation(path);
      setShowUnsavedDialog(true);
    } else {
      navigate(path);
    }
  };


  const handleSaveAndExit = async () => {
    try {
      await forceSave();
      setShowUnsavedDialog(false);
      if (pendingNavigation) {
        navigate(pendingNavigation);
        setPendingNavigation(null);
      }
    } catch (error) {
      toast.error('Não foi possível salvar antes de sair. Suas alterações estão preservadas localmente — tente novamente.');
    }
  };

  const handleDiscardChanges = () => {
    setShowUnsavedDialog(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  };

  const handleCancelUnsavedDialog = () => {
    setShowUnsavedDialog(false);
    setPendingNavigation(null);
  };


  const handleFieldChange = <K extends keyof MeetingEditorState>(
    field: K,
    value: MeetingEditorState[K]
  ) => {
    if (!meeting) return;
    setMeetingLocalState((prev) => ({ ...prev, [field]: value }));
  };


  const handleExportPDF = async () => {
    if (!meeting || !project) return;

    if (hasUnsavedChanges) {
      await forceSave();
    }

    navigate(`/projects/${projectId}/meetings/${meetingId}/preview`);
  };

  const handleCloneMeeting = async () => {
    if (!meeting) return;

    try {
      // Salvar alterações pendentes primeiro
      if (hasUnsavedChanges) {
        await forceSave();
      }

      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;

      const clonedMeeting = await meetingService.cloneMeeting(meetingId, {
        date: dateString,
      });

      toast.success(t('meetings.meeting_cloned'));
      navigate(`/projects/${projectId}/meetings/${clonedMeeting.id}`);
    } catch (error) {
      const msg = error instanceof Error ? error.message : '';
      if (msg.includes('Network') || msg.includes('network') || msg.includes('ERR_NETWORK')) {
        toast.error('Não foi possível duplicar a ata — verifique sua conexão com a internet.');
      } else {
        toast.error(msg || 'Não foi possível duplicar a ata. Tente novamente.');
      }
    }
  };


  const handleExportTopicHistory = (topicoId: string) => {
    const topico = topicsHook.topicos.find((t) => t.id === topicoId);
    if (topico) {
      setHistoryDrawerTopic(topico);
    }
  };

  const handleFinalizeMeeting = async () => {

    if (!meetingLocalState.number || meetingLocalState.number <= 0) {
      toast.error('Número da ata é obrigatório para finalizar. Preencha o campo de número.');
      return;
    }

    if (!meetingLocalState.date || !meetingLocalState.date.trim()) {
      toast.error('Data da ata é obrigatória para finalizar. Selecione uma data.');
      return;
    }

    if (!meetingLocalState.location || !meetingLocalState.location.trim()) {
      toast.error('Local da reunião é obrigatório para finalizar. Informe onde a reunião ocorreu.');
      return;
    }


    const dateObj = new Date(meetingLocalState.date);
    if (isNaN(dateObj.getTime())) {
      toast.error('Data inválida. Use o seletor de data ou o formato AAAA-MM-DD.');
      return;
    }


    setShowFinalizeDialog(true);
  };

  const confirmFinalizeMeeting = async () => {
    setShowFinalizeDialog(false);

    try {

      if (hasUnsavedChanges) {
        await forceSave();
      }

      await meetingService.updateMeeting(meetingId, {
        status: "final",
      });
      queryClient.invalidateQueries({ queryKey: ['meetings', 'project', projectId] });
      toast.success(t('editor.meeting_finalized'));
      navigate(`/projects/${projectId}/meetings/${meetingId}/preview`);
    } catch (error) {
      const msg = error instanceof Error ? error.message : '';
      if (msg.includes('Network') || msg.includes('network') || msg.includes('ERR_NETWORK')) {
        toast.error('Não foi possível finalizar a ata — verifique sua conexão com a internet.');
      } else {
        toast.error(msg || 'Não foi possível finalizar a ata. Verifique os campos obrigatórios e tente novamente.');
      }
    }
  };

  const queryClient = useQueryClient();

  const handleReopenMeeting = async () => {
    try {
      await meetingService.updateMeeting(meetingId, { status: "draft" });
      await refetch();
      // Invalida o cache da lista de atas para que ao voltar mostre o status atualizado
      queryClient.invalidateQueries({ queryKey: ['meetings', 'project', projectId] });
      toast.success('Ata reaberta para edição.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Não foi possível reabrir a ata.');
    }
  };


  const confirmDelete = async () => {
    if (!deleteConfirm) return;

    const { type, id } = deleteConfirm;
    setDeleteConfirm(null);

    switch (type) {
      case 'participant':
        await participantsHook.handleDeleteParticipant(id);
        break;
      case 'topic':
        await topicsHook.handleDeleteTopic(id);
        break;
      case 'item':
        await itemsHook.handleDeleteItem(id);
        break;
      case 'desenvolvimento':
        await itemsHook.handleDeleteDesenvolvimento(id);
        break;
    }
  };




  const handleCreateTopic = useCallback(async () => {

    const trimmedTitle = (inputValueRef.current || newTopicoTitle).trim();
    
    if (!trimmedTitle) {
      toast.error('Título do tópico é obrigatório. Digite um título antes de criar.');
      return;
    }

    setIsCreatingTopic(true);
    try {

      const createdTopic = await topicsHook.handleCreateTopic(trimmedTitle);
      setNewTopicoTitle("");
      inputValueRef.current = "";
      setIsModalOpen(false);
      

      if (createdTopic?.id) {
        setTimeout(() => {
          const topicElement = document.getElementById(`topic-${createdTopic.id}`);
          if (topicElement) {
            topicElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            

            setTimeout(() => {
              const firstInput = topicElement.querySelector('input, textarea') as HTMLInputElement;
              if (firstInput) {
                firstInput.focus();
              }
            }, 500);
          }
        }, 100);
      }
      
      toast.success(t('editor.topic_created'));
    } catch (error) {
      const msg = error instanceof Error ? error.message : '';
      if (msg.includes('Network') || msg.includes('network') || msg.includes('ERR_NETWORK')) {
        toast.error('Não foi possível criar o tópico — verifique sua conexão com a internet.');
      } else {
        toast.error(msg || 'Não foi possível criar o tópico. Tente novamente.');
      }
    } finally {
      setIsCreatingTopic(false);
    }
  }, [newTopicoTitle, topicsHook]);
  

  const handleCancelAddTopic = useCallback(() => {
    setNewTopicoTitle("");
    inputValueRef.current = "";
    setIsModalOpen(false);
  }, []);
  

  const handleFormSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleCreateTopic();
  }, [handleCreateTopic]);
  

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {

    if (e.nativeEvent.isComposing) {
      return;
    }
    
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      handleCreateTopic();
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      handleCancelAddTopic();
    }
  }, [handleCreateTopic, handleCancelAddTopic]);

  if (loading || !meeting || !project) {
    return <MeetingEditorSkeleton />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="mb-4">
            <svg
              className="mx-auto h-12 w-12 text-red-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">
            {t('editor.error_loading_meeting')}
          </h3>
          <p className="text-destructive mb-6">{error}</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() =>
                navigateWithCheck(`/projects/${projectId}/meetings`)
              }
              className="px-4 py-2 border border-border text-foreground rounded hover:bg-muted transition-colors"
            >
              {t('editor.back_to_list')}
            </button>
            <button
              onClick={refetch}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
            >
              {t('try_again')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex h-dvh min-h-0 min-w-0 flex-col overflow-hidden bg-muted/30"
      style={{
        fontFamily: "system-ui",
        fontSize: "14px",
        lineHeight: "1.5",
      }}
    >
      {}
      {showOnboarding && (
        <MeetingOnboarding
          onClose={() => setShowOnboarding(false)}
          onDontShowAgain={() => {
            localStorage.setItem('meeting_onboarding_disabled', 'true');
            setShowOnboarding(false);
          }}
        />
      )}

      {}
      {guideStep >= 0 && (
        <MeetingGuide
          step={guideStep}
          onNext={() => setGuideStep((s) => s + 1)}
          onSkip={() => setGuideStep(-1)}
          onComplete={() => setGuideStep(-1)}
        />
      )}

      {}
      <Header
        breadcrumbs={[
          { label: t('dashboard.title'), href: '/dashboard' },
          { label: project?.name || t('project.new_project'), href: `/projects/${projectId}/meetings` },
          { label: meeting?.name || `${t('meetings.meeting_number', { number: meeting?.number || '' })}` }
        ]}
      />

      {/* Barra de aviso para modo somente visualização */}
      {!canEdit && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 text-amber-600"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
          <span className="text-amber-800 text-sm font-medium">
            {t('editor.view_only_mode', 'Você está no modo de visualização. Apenas colaboradores com permissão de edição podem modificar esta ata.')}
          </span>
        </div>
      )}

      {}
      <MeetingHeader
        meeting={meeting}
        project={project}
        projectId={projectId}
        saveStatus={saveStatus}
        meetingName={meetingLocalState.name}
        onNameChange={(name) => handleFieldChange("name", name)}
        legenda={meetingLocalState.legenda}
        observacoes={meetingLocalState.observacoes}
        responsavelId={meetingLocalState.responsavel_id}
        responsibleOptions={responsibleOptions}
        onLegendaChange={(value) => handleFieldChange("legenda", value)}
        onObservacoesChange={(value) => handleFieldChange("observacoes", value)}
        onResponsavelChange={(value) => handleFieldChange("responsavel_id", value)}
        currentDate={meetingLocalState.date}
        currentHoraInicio={meetingLocalState.hora_inicio || null}
        currentHoraFim={meetingLocalState.hora_fim || null}
        onSave={() => forceSave()}
        onFinalize={handleFinalizeMeeting}
        onReopen={handleReopenMeeting}
        onBack={() => navigateWithCheck(`/projects/${projectId}/meetings`)}
        onExportPDF={handleExportPDF}
        onClone={handleCloneMeeting}
        onEditProjectCode={() => setShowProjectCodeModal(true)}
        canEdit={canEdit}
      />

      <div
        className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden md:flex-row"
      >
        {/* Sidebar - sempre visível, muda largura quando colapsada */}
        <div
          className={cn(
            "z-40 flex min-h-0 flex-shrink-0 flex-col overflow-hidden border-b border-border bg-background md:border-b-0 md:border-r",
            sidebarCollapsed
              ? "h-12 w-full md:h-auto md:w-12"
              : "max-h-48 w-full md:max-h-none md:w-64"
          )}
        >
          <TopicSidebar
            topicos={topicsHook.topicos}
            selectedTopicId={topicsHook.selectedTopicId}
            activeSectionId={activeSection}
            onSelectTopic={topicsHook.setSelectedTopicId}
            onAddTopic={async (title) => {
              await topicsHook.handleCreateTopic(title);
            }}
            onUpdateTopicTitle={topicsHook.handleUpdateTopic}
            onDeleteTopic={(topicoId) => {
              const topico = topicsHook.topicos.find((t) => t.id === topicoId);
              const itensDoTopico = itemsHook.items.filter((i) => i.topico_id === topicoId);
              setDeleteConfirm({
                type: 'topic',
                id: topicoId,
                message: t('editor.delete_topic_message'),
                topicoNome: topico?.titulo,
                topicoItens: itensDoTopico.map((i) => i.descricao),
              });
            }}
            collapsed={sidebarCollapsed}
            onToggleCollapse={() => {
              const newState = !sidebarCollapsed;
              setSidebarCollapsed(newState);
              localStorage.setItem("sidebar-collapsed", String(newState));
            }}
            canEdit={canEdit}
          />
        </div>

        {/* Main content */}
        <main
          className="z-10 min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto"
        >
          <div
            className="relative mx-auto w-full min-w-0 max-w-7xl px-4 py-6 pb-16 sm:px-6"
          >
            {/* Header section */}
            <div id="header-section" className="scroll-mt-20 mb-12">
              <h2
                className="text-xl font-bold mb-6"
                style={{ color: "#013EFB" }}
              >
                {t('editor.meeting_info')}
              </h2>

              <Card>
                <CardContent className="pt-6">
                  {/* Info fields grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <Label htmlFor="meeting-number">{t('editor.meeting_number')}</Label>
                      <Input
                        id="meeting-number"
                        type="number"
                        value={meetingLocalState.number || ""}
                        onChange={(e) =>
                          handleFieldChange("number", parseInt(e.target.value) || 1)
                        }
                        onBlur={(e) => {
                          const newNum = parseInt(e.target.value) || 1;
                          handleSaveMeeting({ number: newNum });
                        }}
                        min="1"
                        className="mt-1.5"
                        disabled={!canEdit}
                        readOnly={!canEdit}
                      />
                    </div>

                    <div>
                      <Label htmlFor="meeting-date">{t('date')}</Label>
                      <Input
                        id="meeting-date"
                        type="date"
                        value={meetingLocalState.date || ""}
                        onChange={(e) => handleFieldChange("date", e.target.value)}
                        className="mt-1.5"
                        disabled={!canEdit}
                        readOnly={!canEdit}
                      />
                    </div>

                    <div>
                      <Label htmlFor="meeting-hora-inicio">{t('editor.meeting_start_time', 'Horário de início')} <span className="text-xs text-muted-foreground">({t('optional')})</span></Label>
                      <Input
                        id="meeting-hora-inicio"
                        type="time"
                        value={meetingLocalState.hora_inicio || ""}
                        onChange={(e) => handleFieldChange("hora_inicio", e.target.value || null)}
                        className="mt-1.5"
                        disabled={!canEdit}
                        readOnly={!canEdit}
                      />
                    </div>

                    <div>
                      <Label htmlFor="meeting-hora-fim">{t('editor.meeting_end_time', 'Horário de fim')} <span className="text-xs text-muted-foreground">({t('optional')})</span></Label>
                      <Input
                        id="meeting-hora-fim"
                        type="time"
                        value={meetingLocalState.hora_fim || ""}
                        onChange={(e) => handleFieldChange("hora_fim", e.target.value || null)}
                        className="mt-1.5"
                        disabled={!canEdit}
                        readOnly={!canEdit}
                      />
                    </div>

                    <div className="sm:col-span-2 lg:col-span-4">
                      <Label htmlFor="meeting-location">{t('editor.meeting_location')}</Label>
                      <Input
                        id="meeting-location"
                        type="text"
                        value={meetingLocalState.location || ""}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val.length <= LIMITS.MEETING_LOCATION_MAX) {
                            handleFieldChange("location", val);
                          } else {
                            handleFieldChange("location", val.slice(0, LIMITS.MEETING_LOCATION_MAX));
                          }
                        }}
                        maxLength={LIMITS.MEETING_LOCATION_MAX}
                        placeholder={t('editor.enter_location')}
                        className="mt-1.5"
                        disabled={!canEdit}
                        readOnly={!canEdit}
                      />
                    </div>

                  </div>

                  {/* Divider */}
                  <div className="border-t border-border my-6" />

                  {/* Participants section */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <Label className="text-base">{t('editor.participants')}</Label>
                      {canEdit && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => participantsHook.setShowParticipantModal(true)}
                        >
                          <Plus size={16} className="mr-1.5" />
                          {t('editor.add_participant')}
                        </Button>
                      )}
                    </div>

                    {participantsHook.participantes.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <p className="text-sm">{t('editor.no_participants')}</p>
                        {canEdit && <p className="text-xs mt-1">{t('editor.add_first_participant')}</p>}
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {participantsHook.participantes.map((p) => (
                          <div
                            key={p.id}
                            className="group flex items-center gap-1.5 pl-3 pr-1.5 py-1.5 bg-muted/50 border border-border rounded-full text-sm hover:bg-muted transition-colors"
                          >
                            <span className="font-medium">{p.nome}</span>
                            {p.empresa && (
                              <span className="text-muted-foreground">
                                · {p.empresa}
                                {p.cargo && <span className="text-xs"> ({p.cargo})</span>}
                              </span>
                            )}
                            {canEdit && (
                              <div className="flex items-center gap-0.5 ml-1">
                                <button
                                  onClick={() => {
                                    participantsHook.setEditingParticipant(p);
                                    participantsHook.setShowParticipantModal(true);
                                  }}
                                  className="p-1 hover:bg-background rounded-full transition-colors opacity-60 hover:opacity-100"
                                  title="Editar"
                                >
                                  <Edit2 size={12} />
                                </button>
                                <button
                                  onClick={() => participantsHook.handleDeleteParticipant(p.id)}
                                  className="p-1 hover:bg-destructive/10 hover:text-destructive rounded-full transition-colors opacity-60 hover:opacity-100"
                                  title="Excluir"
                                >
                                  <Trash2 size={12} />
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {}
            {topicsHook.topicos.length > 0 ? (
              <>
                {topicsHook.topicos.map((topico) => {
                  const itemsForTopic = itemsHook.items.filter(
                    (i) => i.topico_id === topico.id
                  );
                  return (
                    <div
                      key={topico.id}
                      id={`topic-${topico.id}`}
                      className="mb-8 scroll-mt-24"
                    >
                      <TopicSection
                        topico={topico}
                        items={itemsForTopic}
                        desenvolvimentos={itemsHook.desenvolvimentos}
                        meetingNumber={meeting.number}
                        lastMeetingNumber={lastMeetingNumber}
                        meetingDate={meeting.date}
                        canEdit={canEdit}
                        onAddItem={async (topicoId, input) => {
                          await itemsHook.handleCreateItem({
                            descricao: input.descricao,
                            topico_id: topicoId,
                            entidade: input.entidade,
                            prazo: input.prazo,
                            ata_origem_data: meetingLocalState.date || meeting.date,
                            ata_origem_numero: meeting.number,
                          });
                        }}
                        onEditItem={itemsHook.setEditingItem}
                        onDeleteItem={(itemId) => {
                          setDeleteConfirm({
                            type: 'item',
                            id: itemId,
                            message: 'Tem certeza que deseja excluir este item?'
                          });
                        }}
                        onToggleItemStatus={(itemId, concluido) =>
                          itemsHook.handleToggleItemStatus(itemId, concluido)
                        }
                        onAddDesenvolvimento={itemsHook.handleAddDesenvolvimento}
                        onUpdateDesenvolvimento={itemsHook.handleUpdateDesenvolvimento}
                        onDeleteDesenvolvimento={(devId) => {
                          setDeleteConfirm({
                            type: 'desenvolvimento',
                            id: devId,
                            message: 'Tem certeza que deseja excluir este desenvolvimento?'
                          });
                        }}
                        onExportTopicHistory={handleExportTopicHistory}
                      />
                    </div>
                  );
                })}
              </>
            ) : (
              
              <div
                className="mb-12 text-center py-12 bg-card rounded-xl border border-border"
              >
                <div className="max-w-md mx-auto">
                  <div className="mb-4">
                    <svg
                      className="mx-auto h-16 w-16 text-muted-foreground/30"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    {t('editor.no_topics')}
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    {t('editor.add_first_topic')}
                  </p>
                  
                  <button
                    onClick={() => setIsModalOpen(true)}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                  >
                    <Plus size={20} />
                    {t('editor.add_first_topic_button')}
                  </button>
                </div>
              </div>
            )}

            {}
            <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle>{t('editor.new_topic')}</DialogTitle>
                  <DialogDescription>
                    {t('editor.new_topic_description')}
                  </DialogDescription>
                </DialogHeader>
                
                {}
                <form onSubmit={handleFormSubmit}>
                  <div className="grid gap-4 py-4">
                    <div className="grid gap-2">
                      <Label htmlFor="topic-title">{t('editor.topic_title')}</Label>
                      <Input
                        ref={newTopicInputRef}
                        id="topic-title"
                        type="text"
                        value={newTopicoTitle}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value.length <= LIMITS.TOPIC_TITLE_MAX) {
                            setNewTopicoTitle(value);
                            inputValueRef.current = value;
                          } else {
                            const truncated = value.slice(0, LIMITS.TOPIC_TITLE_MAX);
                            setNewTopicoTitle(truncated);
                            inputValueRef.current = truncated;
                          }
                        }}
                        maxLength={LIMITS.TOPIC_TITLE_MAX}
                        onKeyDown={handleKeyDown}
                        placeholder={t('editor.topic_placeholder')}
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck={false}
                      />
                    </div>
                  </div>
                  
                  <DialogFooter>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleCancelAddTopic}
                      disabled={isCreatingTopic}
                    >
                      {t('cancel')}
                    </Button>
                    <Button
                      type="submit"
                      disabled={!newTopicoTitle.trim() || isCreatingTopic}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      {isCreatingTopic ? t('editor.creating_topic') : t('editor.create_topic')}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>

            {/* Galeria de Imagens */}
            <AtaGaleriaEditor
              ataId={meetingId || ""}
              imagens={imagensHook.imagens}
              onImagensChange={imagensHook.setImagens}
              disabled={meeting?.status === "final" || !canEdit}
            />

            {/* Anexos */}
            <div className="mt-8">
              {(() => {
                const isDisabled = meeting?.status === "final" || !canEdit;
                const arr = meetingLocalState.anexos;
                const updateArr = (next: readonly AtaAttachment[]) =>
                  handleFieldChange("anexos", [...next]);
                const updateArrAndSave = async (next: readonly AtaAttachment[]) => {
                  handleFieldChange("anexos", [...next]);
                  await handleSaveMeeting({ anexos: next });
                };
                const addItem = (afterIdx?: number) => {
                  const next = afterIdx === undefined
                    ? [...arr, { nome: "" }]
                    : [
                        ...arr.slice(0, afterIdx + 1),
                        { nome: "" },
                        ...arr.slice(afterIdx + 1),
                      ];
                  updateArrAndSave(next);
                };

                return (
                  <div>
                    {/* Cabeçalho */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-medium text-foreground tracking-tight">
                          {t("editor.attachments_label", "Anexos")}
                        </h3>
                        {arr.length > 0 && (
                          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-muted text-xs text-muted-foreground font-medium">
                            {arr.length}
                          </span>
                        )}
                      </div>
                      {!isDisabled && (
                        <button
                          type="button"
                          onClick={() => addItem()}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-muted-foreground bg-muted hover:bg-muted/80 active:scale-95 rounded-lg transition-all duration-150"
                        >
                          <Plus className="h-3.5 w-3.5" />
                          {t("editor.attachments_add", "Adicionar")}
                        </button>
                      )}
                    </div>

                    {/* Lista */}
                    {arr.length === 0 ? (
                      <div
                        className={`border border-dashed border-border rounded-2xl p-10 text-center ${
                          !isDisabled
                            ? "cursor-pointer hover:border-primary/40 hover:bg-primary/5 active:scale-[0.99] transition-all duration-200 group"
                            : ""
                        }`}
                        onClick={!isDisabled ? () => addItem() : undefined}
                      >
                        <div className="w-10 h-10 mx-auto mb-3 rounded-xl bg-muted group-hover:bg-primary/10 flex items-center justify-center transition-colors duration-200">
                          <Paperclip className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                        </div>
                        <p className="text-sm font-medium text-muted-foreground mb-0.5">
                          {t("editor.attachments_empty_title", "Nenhum anexo")}
                        </p>
                        {!isDisabled && (
                          <p className="text-xs text-muted-foreground/60 font-light">
                            {t("editor.attachments_empty_hint", "Clique para adicionar o primeiro anexo")}
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-1.5">
                        {arr.map((item, idx) => (
                          <AnexoRow
                            ataId={meetingId}
                            key={idx}
                            item={item}
                            idx={idx}
                            isDisabled={isDisabled}
                            onNomeChange={(value) => {
                              const next = arr.map((current, index) =>
                                index === idx ? { ...current, nome: value } : current
                              );
                              updateArr(next);
                            }}
                            onNomeBlur={() => {
                              handleSaveMeeting({ anexos: arr });
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                addItem(idx);
                              }
                              if (e.key === "Backspace" && item.nome === "" && !item.url) {
                                e.preventDefault();
                                updateArrAndSave(arr.filter((_, i) => i !== idx));
                              }
                            }}
                            onUpload={async (file) => {
                              try {
                                const result = await uploadService.uploadAnexo(file);
                                const next = arr.map((current, index) =>
                                  index === idx
                                    ? {
                                        nome: current.nome || result.nome,
                                        url: result.url,
                                      }
                                    : current
                                );
                                await updateArrAndSave(next);
                              } catch (error) {
                                toast.error(error instanceof Error ? error.message : "Erro ao fazer upload do anexo");
                              }
                            }}
                            onRemove={() =>
                              updateArrAndSave(arr.filter((_, i) => i !== idx))
                            }
                            autoFocus={item.nome === "" && idx === arr.length - 1}
                          />
                        ))}

                        {/* Botão adicionar inline */}
                        {!isDisabled && (
                          <button
                            type="button"
                            onClick={() => addItem()}
                            className="w-full flex items-center gap-2 px-4 py-2 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-xl transition-all duration-150 group/add"
                          >
                            <div className="w-5 h-5 rounded-md border border-dashed border-border group-hover/add:border-muted-foreground flex items-center justify-center transition-colors shrink-0">
                              <Plus className="h-3 w-3" />
                            </div>
                            <span className="text-xs">
                              {t("editor.attachments_add_row", "Adicionar anexo")}
                            </span>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </main>
      </div>

      {}
      {itemsHook.editingItem && (
        <ItemEditor
          item={itemsHook.editingItem}
          desenvolvimentos={itemsHook.desenvolvimentos}
          topicos={topicsHook.topicos}
          participantes={participantsHook.participantes.map(p => ({
            id: p.id,
            nome: p.nome,
            email: p.email || ''
          }))}
          meetingNumber={meeting?.number || 0}
          lastMeetingNumber={lastMeetingNumber}
          onSave={itemsHook.handleUpdateItem}
          onDelete={async (itemId) => {
            setDeleteConfirm({
              type: 'item',
              id: itemId,
              message: t('editor.delete_item_message')
            });
          }}
          onClose={() => itemsHook.setEditingItem(null)}
          onAddDesenvolvimento={itemsHook.handleAddDesenvolvimento}
          onUpdateDesenvolvimento={itemsHook.handleUpdateDesenvolvimento}
          onDeleteDesenvolvimento={async (devId) => {
            setDeleteConfirm({
              type: 'desenvolvimento',
              id: devId,
              message: t('editor.delete_development_message')
            });
          }}
        />
      )}

      {}
      <ParticipantDrawer
        open={participantsHook.showParticipantModal}
        editingParticipant={participantsHook.editingParticipant}
        currentParticipants={participantsHook.participantes}
        projectId={projectId || ""}
        contatos={contatos}
        form={participantsHook.participantForm}
        onClose={() => {
          participantsHook.setShowParticipantModal(false);
          participantsHook.resetParticipantForm();
        }}
        onFormChange={(field, value) => {
          participantsHook.setParticipantForm({ [field]: value });
        }}
        onSave={
          participantsHook.editingParticipant
            ? participantsHook.handleUpdateParticipant
            : participantsHook.handleCreateParticipant
        }
        onBulkAdd={participantsHook.handleBulkAddParticipants}
        onAddWithDetails={participantsHook.handleAddWithDetails}
        onEdit={(participant) => {
          participantsHook.setEditingParticipant(participant);
        }}
        onDelete={(participantId) => {
          participantsHook.handleDeleteParticipant(participantId);
        }}
        onCancelEdit={() => {
          participantsHook.setEditingParticipant(null);
          participantsHook.resetParticipantForm();
        }}
      />

      {}
      {historyDrawerTopic && (
        <TopicHistoryDrawer
          open={!!historyDrawerTopic}
          onOpenChange={(open) => !open && setHistoryDrawerTopic(null)}
          topicTitle={historyDrawerTopic.titulo}
          items={itemsHook.items.filter((i) => i.topico_id === historyDrawerTopic.id)}
          desenvolvimentos={itemsHook.desenvolvimentos.filter((d) =>
            itemsHook.items.some((i) => i.topico_id === historyDrawerTopic.id && i.id === d.item_id)
          )}
        />
      )}

      {}
      {showUnsavedDialog && (
        <UnsavedChangesDialog
          onSaveAndExit={handleSaveAndExit}
          onDiscard={handleDiscardChanges}
          onCancel={handleCancelUnsavedDialog}
        />
      )}

      {}
      {showFinalizeDialog && (
        <ConfirmDialog
          title={t('meetings.finalize_confirm_title')}
          message={t('meetings.finalize_confirm_message')}
          confirmText={t('meetings.finalize')}
          cancelText={t('cancel')}
          onConfirm={confirmFinalizeMeeting}
          onCancel={() => setShowFinalizeDialog(false)}
          type="warning"
        />
      )}

      {}
      {deleteConfirm && (
        <ConfirmDialog
          title={t('editor.confirm_delete_title')}
          message={deleteConfirm.message}
          confirmText={t('delete')}
          cancelText={t('cancel')}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteConfirm(null)}
          type="danger"
        >
          {deleteConfirm.type === 'topic' && deleteConfirm.topicoNome && (
            <div className="text-sm text-muted-foreground space-y-2">
              <p>
                Tópico: <span className="font-semibold text-foreground">{deleteConfirm.topicoNome}</span>
              </p>
              {deleteConfirm.topicoItens && deleteConfirm.topicoItens.length > 0 ? (
                <div>
                  <p className="mb-1">
                    Os seguintes <span className="font-semibold text-destructive">{deleteConfirm.topicoItens.length} {deleteConfirm.topicoItens.length === 1 ? 'item' : 'itens'}</span> serão excluídos permanentemente:
                  </p>
                  <ul className="max-h-36 overflow-y-auto space-y-1 rounded-md border border-border bg-muted/50 p-2">
                    {deleteConfirm.topicoItens.map((descricao, idx) => (
                      <li key={idx} className="flex gap-2 text-xs text-foreground">
                        <span className="text-destructive font-bold mt-px">&bull;</span>
                        <span className="line-clamp-2">{descricao}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="text-xs">Este tópico não possui itens vinculados.</p>
              )}
            </div>
          )}
        </ConfirmDialog>
      )}

      {}
      {project && (
        <ProjectCodeModal
          open={showProjectCodeModal}
          onClose={() => setShowProjectCodeModal(false)}
          projectName={project.name}
          currentCode={project.project_code || ''}
          onSave={async (code) => {
            await projectService.updateProject(project.id, {
              project_code: code || undefined,
            });
            await refetch();
            toast.success('Código da obra atualizado');
          }}
        />
      )}
    </div>
  );
}
